import React, { useEffect, useRef, useState } from "react";
import Alert from "react-bootstrap/Alert";
import ReactLoading from "react-loading";
import { useNavigate } from "react-router-dom";
import { FaArrowDown } from "react-icons/fa";

import styles from "./Home.module.css";

import { useBooking } from "../../hooks/useBooking.js";
import { useCustomerValidation } from "../../hooks/useCustomerValidation.js";
import { usePayment } from "../../hooks/usePayment.js";
import { usePricing } from "../../hooks/usePricing.js";
import { useClient } from "../../hooks/useClient.js";
import { useCalendarSelections } from "../../hooks/useCalendarSelections.js";

import RenterInfoForm from "../../components/renterInfoForm/renterInfoForm.js";
import UnitPaymentForm from "../../components/unitPaymentForm/unitPaymentForm.js";
import PriceDisplay from "../../components/priceDisplay/priceDisplay.js";
import BookingCalendar from "../../components/bookingCalendar/bookingCalendar.js";
import ProgressBar from "../../components/progressBar/progressBar.js";
import NavBar from "../../components/navBar/navBar.js";
import RenterInfoDisplay from "../../components/renterInfoDisplay/renterInfoDisplay.js";
import BookingComplete from "../../components/bookingComplete/bookingComplete.js";
import Header from "../../components/header/header.js";
import ErrorMessage from "../../components/shared/ErrorMessage.js";


function Home() {
  const [step, setStep] = useState(0); 
  const [bookingErrorMessage, setBookingErrorMessage] = useState("");
  const [confirmedBooking, setConfirmedBooking] = useState("");
  const selectionSectionRef = useRef(null);
  const navigate = useNavigate();

// Custom Hooks
  const { clientData, clientError, clientIsLoading} = useClient();
  const { checkoutReservation } = useBooking();
  const { customer, customerFormErrors, setCustomer } = useCustomerValidation();
  const {
    squareCredentials,
    squareCredentialsIsLoading,
    squareCredentialsError,
  } = usePayment();
  const { selectedDates, selectedSlots, addDate, removeDate, addSlot, removeSlot, clearDates } = useCalendarSelections();
  const { totals, appliedDiscount, manualEnteredDiscounts, addManualDiscount } = usePricing({ selectedSlots });

  const handleBookingSubmission = async (paymentToken, paymentAmount, noPayment, verifiedBuyer) => {
    setStep(5); // set loading states

    try {
      const checkout = await checkoutReservation.mutateAsync({
        customer,
        selectedSlots,
        discountCode: appliedDiscount?.code || null,
        payment: paymentToken && !noPayment
          ? {
              type: "square",
              sourceId: paymentToken,
              verificationToken: verifiedBuyer?.token,
            }
          : { type: "none" },
        createdBy: "customer",
      });

      setConfirmedBooking(checkout.bookingId);
      navigate(checkout.confirmationUrl || `/${clientData.id}/confirmation?booking_id=${checkout.bookingId}&confirmationOnly=true`);
    } catch (error) {
      console.error("Checkout failed:", error);
      setStep(4);
      setBookingErrorMessage(error.response?.data?.message || "Booking failed. Please Contact Us.");
    }

    return;
  }

  useEffect(() => {
    if (step === 2 && selectedSlots?.length < 1) {
      setStep(0);
    }
  }, [step, selectedSlots])

  const shouldScrollToSelections = step === 0 && selectedDates.length > 0 && selectedSlots.length === 0;

  const scrollToSelections = () => {
    selectionSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };


  if (clientIsLoading) return (
    <div className="centering-container">
      <ReactLoading type="spin" color="#000" />
    </div>
  );

  if (clientError) {
    return (
      <ErrorMessage 
      title="We're experiencing issues right now."
      error={`The site may be undergoing maintenance or updates. Please check back shortly.
          If this continues, feel free to reach out to our team for help.`}
      />
    )
  }

  if (clientData?.active === false) {
    return (
      <ErrorMessage 
      title="This site is currently under maintenance."
      error={`Please contact us for more information or try again later.`}
      />
    )
  }
  

  return (
    <div className="home-page">
      {clientData?.logo && <Header 
        name={clientData.companyName}
        logoUrl={clientData.logo}
        websiteUrl={clientData.website}
      />}

      <ProgressBar
        step={step}
        setStep={setStep}
        selectionsDone={selectedSlots?.length > 0}
        infoDone={
          customer.lastName !== "" &&
          Object.values(customerFormErrors).length === 0
        }
        complete={step === 3}
        error={step === 4}
      />
      <div className={`${styles.content} narrow-container`}>

          {/* booking calendar */}
          {step === 0 &&
          <>

            <BookingCalendar
              selectedDates={selectedDates}
              selectedSlots={selectedSlots}
              addDate={addDate}
              removeDate={removeDate}
              clearDates={clearDates}
              addSlot={addSlot}
              removeSlot={removeSlot}
              clientPhone={clientData.phone}
              selectionSectionRef={selectionSectionRef}
            />

            {selectedSlots.length > 0 && <PriceDisplay
              totals={totals}
              selectedSlots={selectedSlots}
              setStep={setStep}
              appliedDiscount={appliedDiscount}
              addManualDiscount={addManualDiscount}
              removeSlot={removeSlot}
              manualEnteredDiscounts={manualEnteredDiscounts}
            />}
          </>
        }

        {step === 1 && (
          <>
            <PriceDisplay
              totals={totals}
              selectedSlots={selectedSlots}
              setStep={setStep}
              appliedDiscount={appliedDiscount}
              addManualDiscount={addManualDiscount}
              removeSlot={removeSlot}
            />

            <RenterInfoForm
              customer={customer}
              setCustomer={setCustomer}
              errors={customerFormErrors}
            />
          </>
        )}

        {step === 2 && (
          <>
            <PriceDisplay
              totals={totals}
              selectedSlots={selectedSlots}
              setStep={setStep}
              appliedDiscount={appliedDiscount}
              addManualDiscount={addManualDiscount}
              removeSlot={removeSlot}
            />

            <RenterInfoDisplay renterInfo={customer} setStep={setStep} />

            <UnitPaymentForm
              totals={totals}
              setStep={setStep}
              squareCredentials={squareCredentials}
              squareCredentialsIsLoading={squareCredentialsIsLoading}
              squareCredentialsError={squareCredentialsError}
              selectedSlots={selectedSlots}
              terms={clientData.terms}
              handleBookingSubmission={handleBookingSubmission}
            />
          </>
        )}

        {step === 3 && (
          <>
            <PriceDisplay
              hideDiscountForm={true}
              totals={totals}
              selectedSlots={selectedSlots}
              setStep={setStep}
              appliedDiscount={appliedDiscount}
              addManualDiscount={addManualDiscount}
              hideEdit={true}
            />

            <RenterInfoDisplay renterInfo={customer} />

            <BookingComplete email={customer.email} bookingId={confirmedBooking}/>
          </>
        )}

        {step === 4 && (
          <>
            <Alert variant={"danger"}>
              <p>
                {confirmedBooking && (
                  <span>
                    Booking ID: {confirmedBooking} <br />
                  </span>
                )}
                <strong>Booking Error:</strong> {bookingErrorMessage}
              </p>
            </Alert>
          </>
        )}

        {step === 5 && (
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
          <div className="mb-4">
            <ReactLoading type="spin" color="#6c757d" height={60} width={60} />
          </div>
          <div className="alert alert-info text-center shadow-sm px-4" style={{ maxWidth: "500px" }}>
            <strong>Booking in Progress</strong><br />
            Please do not leave this page while we finalize your booking. Leaving this page may cause your booking to be lost.
          </div>
        </div>        
        )}
      </div>
      {step < 2 && (
        <NavBar
          step={step}
          setStep={setStep}
          actionLabel={shouldScrollToSelections ? (
            <span className="continue-btn-content">
              <FaArrowDown aria-hidden="true" />
              View Available Boats
            </span>
          ) : null}
          onAction={shouldScrollToSelections ? scrollToSelections : null}
          actionClassName={shouldScrollToSelections ? "scroll-to-selection-btn" : ""}
          disabled={() => {
            if (shouldScrollToSelections) {
              return false;
            }

            if (step === 0 && selectedSlots?.length > 0) {
              return false;
            }

            if (
              step === 1 &&
              customer.firstName !== "" &&
              Object.values(customerFormErrors)?.length === 0
            ) {
              return false;
            }

            return true;
          }}
        />
      )}
    </div>
  );

  //
}

export default Home;
